import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import api from '../../utils/api';
import AdminHeader from '../../components/AdminHeader';
import './AdminDashboard.css';

import { getStatusConfig } from '../../utils/orderStatusConstants';

const formatCurrency = (value) => {
  const number = Number(value) || 0;
  return `฿${number.toLocaleString('th-TH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('th-TH');
};

export default function AdminDashboard({ admin, logout, isSidebarCollapsed, toggleSidebar }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    todayRevenue: 0,
    todayOrders: 0,
    pendingOrders: 0,
    newCustomersThisMonth: 0
  });
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isSlidePanelOpen, setIsSlidePanelOpen] = useState(false);

  // Redirect to login if admin is not logged in
  useEffect(() => {
    if (!admin) {
      navigate('/admin/login');
    }
  }, [admin, navigate]);

  const fetchDashboardData = useCallback(async () => {
    // Don't fetch if admin is not logged in
    if (!admin) {
      setIsLoading(false);
      return;
    }

    try {
      const [ordersRes, customersRes] = await Promise.all([
        api.get('/api/orders'),
        api.get('/api/admin/customers')
      ]);

      const allOrders = ordersRes.data || [];
      const allCustomers = customersRes.data || [];

      // Calculate today's date range
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayEnd = new Date(today);
      todayEnd.setHours(23, 59, 59, 999);

      // Calculate this month's date range
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);

      // Filter today's orders
      const todayOrders = allOrders.filter((order) => {
        const orderDate = new Date(order.order_date);
        return orderDate >= today && orderDate <= todayEnd;
      });

      // Calculate today's revenue
      const todayRevenue = todayOrders.reduce(
        (sum, order) => sum + Number(order.total_amount || 0),
        0
      );

      // Filter new customers this month
      const newCustomersThisMonth = allCustomers.filter((customer) => {
        if (!customer.createdAt) return false;
        const customerDate = new Date(customer.createdAt);
        return customerDate >= monthStart && customerDate <= monthEnd;
      }).length;

      // Filter pending orders
      const pendingOrders = allOrders.filter((order) => order.order_status === 'pending');

      setStats({
        todayRevenue,
        todayOrders: todayOrders.length,
        pendingOrders: pendingOrders.length,
        newCustomersThisMonth
      });

      // Sort orders by date (newest first) and take latest 5
      setOrders(
        allOrders
          .slice()
          .sort((a, b) => new Date(b.order_date).getTime() - new Date(a.order_date).getTime())
          .slice(0, 5)
      );

      setLastFetchedAt(new Date());
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      
      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Token is invalid or expired, redirect to login
        logout();
        navigate('/admin/login');
        return;
      }
      
      setIsLoading(false);
    }
  }, [admin, logout, navigate]);

  useEffect(() => {
    // Only fetch data if admin is logged in
    if (admin) {
      fetchDashboardData();
    }
  }, [admin, fetchDashboardData]);

  const handleViewOrderDetails = async (orderId) => {
    try {
      const response = await api.get(`/api/orders/${orderId}`);
      setSelectedOrder(response.data);
      setIsSlidePanelOpen(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      
      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
        navigate('/admin/login');
      }
    }
  };

  const handleCloseSlidePanel = () => {
    setIsSlidePanelOpen(false);
    setSelectedOrder(null);
  };


  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    return (
      <span className="dashboard-status-badge" style={{ backgroundColor: config.color, color: config.textColor }}>
        {config.label}
      </span>
    );
  };

  const kpiCards = useMemo(
    () => [
      {
        key: 'todayRevenue',
        label: 'ยอดขายรวมวันนี้',
        value: formatCurrency(stats.todayRevenue),
        icon: 'bi bi-currency-dollar',
        gradient: 'linear-gradient(135deg, rgba(77, 166, 255, 0.15) 0%, rgba(29, 185, 84, 0.15) 100%)',
        borderColor: 'rgba(77, 166, 255, 0.3)'
      },
      {
        key: 'todayOrders',
        label: 'คำสั่งซื้อใหม่วันนี้',
        value: `${stats.todayOrders.toLocaleString('th-TH')} รายการ`,
        icon: 'bi bi-cart-plus',
        gradient: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.15) 100%)',
        borderColor: 'rgba(255, 193, 7, 0.3)'
      },
      {
        key: 'pendingOrders',
        label: 'คำสั่งซื้อรออนุมัติ',
        value: `${stats.pendingOrders.toLocaleString('th-TH')} รายการ`,
        icon: 'bi bi-hourglass-split',
        gradient: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 193, 7, 0.15) 100%)',
        borderColor: 'rgba(255, 215, 0, 0.3)'
      },
      {
        key: 'newCustomers',
        label: 'ลูกค้าใหม่เดือนนี้',
        value: `${stats.newCustomersThisMonth.toLocaleString('th-TH')} คน`,
        icon: 'bi bi-people',
        gradient: 'linear-gradient(135deg, rgba(147, 112, 219, 0.15) 0%, rgba(156, 39, 176, 0.15) 100%)',
        borderColor: 'rgba(147, 112, 219, 0.3)'
      }
    ],
    [stats]
  );


  if (isLoading) {
    return (
      <div className="admin-dashboard">
        <AdminHeader
          admin={admin}
          onLogout={logout}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
        <div className="admin-dashboard__loading">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3 text-muted">กำลังรวบรวมข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <AdminHeader
        admin={admin}
        onLogout={logout}
        isCollapsed={isSidebarCollapsed}
        onToggleSidebar={toggleSidebar}
      />

      <Container fluid className="admin-dashboard__container">
        {/* Header Section */}
        <section className="admin-dashboard__header">
          <div className="admin-dashboard__header-content">
            <div>
              <h1 className="admin-dashboard__title">Sangsawang Dashboard</h1>
              <p className="admin-dashboard__subtitle">ภาพรวมธุรกิจของคุณ</p>
            </div>
            <div className="admin-dashboard__header-meta">
              <span className="admin-dashboard__date">
                <i className="bi bi-calendar3"></i>
                {new Date().toLocaleDateString('th-TH', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              {lastFetchedAt && (
                <span className="admin-dashboard__last-updated">
                  <i className="bi bi-clock"></i>
                  อัปเดตล่าสุด: {lastFetchedAt.toLocaleTimeString('th-TH')}
                </span>
              )}
            </div>
          </div>

        </section>

        {/* KPI Cards */}
        <section className="admin-dashboard__kpi">
          <Row className="g-2">
            {kpiCards.map((card) => (
              <Col key={card.key} xs={12} sm={6} lg={3}>
                <div
                  className="admin-dashboard__kpi-card"
                  style={{
                    background: card.gradient,
                    borderColor: card.borderColor
                  }}
                >
                  <div className="admin-dashboard__kpi-icon">
                    <i className={card.icon}></i>
                  </div>
                  <div className="admin-dashboard__kpi-content">
                    <span className="admin-dashboard__kpi-label">{card.label}</span>
                    <strong className="admin-dashboard__kpi-value">{card.value}</strong>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </section>

        {/* Main Content - Order Overview */}
        <Card className="admin-dashboard__panel">
          <div className="admin-dashboard__panel-header">
            <div>
              <h2>คำสั่งซื้อล่าสุด</h2>
              <p>5 รายการล่าสุดที่เข้ามาในระบบ</p>
            </div>
            <Link to="/admin/orders" className="admin-dashboard__panel-link">
              ดูทั้งหมด <i className="bi bi-arrow-right"></i>
            </Link>
          </div>
          <div className="admin-dashboard__panel-body">
            {orders.length > 0 ? (
              <Table responsive hover className="admin-dashboard__table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>ลูกค้า</th>
                    <th>ยอดรวม</th>
                    <th>สถานะ</th>
                    <th>วันที่</th>
                    <th className="text-center">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.order_id}>
                      <td className="fw-semibold">#{order.order_id}</td>
                      <td>
                        <div className="admin-dashboard__customer-info">
                          <strong>
                            {order.customer_fname} {order.customer_lname}
                          </strong>
                          <span>{order.customer_tel || '-'}</span>
                        </div>
                      </td>
                      <td className="admin-dashboard__amount">{formatCurrency(order.total_amount)}</td>
                      <td>{getStatusBadge(order.order_status)}</td>
                      <td>{formatDate(order.order_date)}</td>
                      <td className="text-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewOrderDetails(order.order_id)}
                          className="d-inline-flex align-items-center justify-content-center"
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="admin-dashboard__empty-state">
                <i className="bi bi-clipboard-minus"></i>
                <strong>ยังไม่มีคำสั่งซื้อใหม่</strong>
                <p>เมื่อมีคำสั่งซื้อเข้ามา จะเห็นรายการล่าสุดที่นี่ทันที</p>
              </div>
            )}
          </div>
        </Card>
      </Container>

      {/* Slide Panel for Order Details */}
      <div className={`admin-dashboard__slide-panel ${isSlidePanelOpen ? 'is-open' : ''}`}>
        <div className="admin-dashboard__slide-panel-header">
          <h3>
            <i className="bi bi-receipt"></i>
            รายละเอียดคำสั่งซื้อ {selectedOrder ? `#${selectedOrder.order_id}` : ''}
          </h3>
          <button
            className="admin-dashboard__slide-panel-close"
            onClick={handleCloseSlidePanel}
            aria-label="ปิด"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        {selectedOrder && (
          <div className="admin-dashboard__slide-panel-body">
            <div className="admin-dashboard__detail-section">
              <h4>
                <i className="bi bi-person"></i>
                ข้อมูลลูกค้า
              </h4>
              <div className="admin-dashboard__detail-content">
                <div className="admin-dashboard__detail-row">
                  <span>ชื่อ:</span>
                  <strong>
                    {selectedOrder.customer_fname} {selectedOrder.customer_lname}
                  </strong>
                </div>
                <div className="admin-dashboard__detail-row">
                  <span>เบอร์:</span>
                  <strong>{selectedOrder.customer_tel || '-'}</strong>
                </div>
                <div className="admin-dashboard__detail-row">
                  <span>อีเมล:</span>
                  <strong>{selectedOrder.customer_email || '-'}</strong>
                </div>
              </div>
            </div>

            <div className="admin-dashboard__detail-section">
              <h4>
                <i className="bi bi-box-seam"></i>
                รายการสินค้า
              </h4>
              <div className="admin-dashboard__detail-content">
                {selectedOrder.details && selectedOrder.details.length > 0 ? (
                  <div className="admin-dashboard__detail-items">
                    {selectedOrder.details.map((item) => (
                      <div key={item.order_detail_id} className="admin-dashboard__detail-item">
                        <div className="admin-dashboard__detail-item-name">{item.product_name}</div>
                        <div className="admin-dashboard__detail-item-meta">
                          <span>x{item.quantity}</span>
                          <strong>{formatCurrency(item.price)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">ไม่มีรายการสินค้า</p>
                )}
              </div>
            </div>

            <div className="admin-dashboard__detail-section">
              <h4>
                <i className="bi bi-currency-dollar"></i>
                สรุปยอด
              </h4>
              <div className="admin-dashboard__detail-content">
                <div className="admin-dashboard__detail-row">
                  <span>ยอดรวม:</span>
                  <strong className="admin-dashboard__detail-total">
                    {formatCurrency(selectedOrder.total_amount)}
                  </strong>
                </div>
              </div>
            </div>

            <div className="admin-dashboard__detail-actions">
              <Button variant="primary" onClick={() => navigate(`/admin/orders`)}>
                <i className="bi bi-arrow-right me-1"></i>
                ไปที่หน้าจัดการคำสั่งซื้อ
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Slide Panel Overlay */}
      {isSlidePanelOpen && (
        <div className="admin-dashboard__slide-panel-overlay" onClick={handleCloseSlidePanel}></div>
      )}
    </div>
  );
}
